import {
  type DataGridPremiumProps,
  GRID_TREE_DATA_GROUPING_FIELD,
  type GridCallbackDetails,
  type GridFilterModel,
  type GridGroupNode,
  type GridPaginationModel,
  type GridSortModel,
  type GridValidRowModel,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { useDataRouterContext } from "src/lib/react-router/useDataRouterContext.ts";
import { useContentLocale } from "src/auth/hooks/useContentLocale.tsx";
import {
  keepPreviousData,
  type UseQueryOptions,
  useSuspenseQuery,
} from "@tanstack/react-query";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  mapGridStateToPersistedState,
  type PersistedGridState,
} from "src/system/useResourceList/utils/mapGridStateToPersistedState.tsx";
import { useUrlState } from "@exonn/use-url-state";
import { deepEq, removeItem, unique } from "@exonn/utils";

import type { GridEventListener } from "@mui/x-data-grid";
import type { DataGridProps } from "src/components/DataGrid/DataGrid.tsx";

import { useList } from "src/system/useList.tsx";
import type { ValidGetListResponse } from "src/system/SystemProvider.tsx";
import { useResourceStorageKeys } from "src/framework/useResourceStorageKeys.tsx";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { useSnackbar } from "src/App/App.hooks.ts";
import { logger } from "src/logger";
import { useUser } from "src/auth/auth.ts";
import { useUserDataState } from "src/user-data/useUserDataState.ts";
import {
  DataGridExpandedRowsSchema,
  DataGridPersistedStateSchema,
} from "src/user-data/schemas.ts";
import type { DataGridExpandedRowsPersistedState } from "src/user-data/types.ts";
import { useGlobalSettings } from "src/hooks/use-global-settings.ts";

import { useProductGridProps } from "src/system/useResourceList/useProductGridProps.tsx";
import { useB2bGateOrderGridProps } from "src/system/useResourceList/useB2bGateOrderProps.tsx";
import { getDataGridCustomMapper } from "src/system/getDataGridCustomMapper.ts";

import { useSearchParams } from "react-router-dom";
import { ResourceEnum } from "src/constants/constants.ts";
import { useClientConfig } from "src/client-config/useClientConfig.ts";
import { resourceListConfigQuery } from "src/system/resourceListConfigQuery.ts";
import { listTranslationsQuery } from "src/system/listTranslationsQuery.ts";
import { prepareFilterModel } from "src/system/useResourceList/utils/prepareFilterModel.tsx";
import { mergeFilterModels } from "src/components/DataGrid/mergeFilterModels.ts";
import { getResourceI18nNamespace } from "src/system/getResourceI18nNamespace.ts";
import { SelectV2 } from "src/components/SelectV2/SelectV2.tsx";
import { useErpTranslation } from "src/system/useErpTranslation.ts";
import { createMapRowsResult } from "src/system/useResourceList/utils/createMapRowResult.ts";
import { convertGridFilterModelToUrlFilterModel } from "src/system/useResourceList/utils/convertGridFilterModelToUrlFilterModel.ts";
import { convertGridSortModelToUrlSortModel } from "src/system/useResourceList/utils/convertGridSortModelToUrlSortModel.ts";
import { convertUrlStateToGridState } from "src/system/useResourceList/utils/convertUrlStateToGridState.ts";
import {
  ResourceListSearchParams,
  type UrlState,
} from "src/system/useResourceList/useResourceList.types.ts";
import { filterModelWithoutIds } from "src/system/useResourceList/utils/filterModelWithoutIds.ts";
import { hideIdColumnInInitialState } from "src/system/useResourceList/utils/hideIdColumnInInitialState.ts";
import { useBankTransactionGridProps } from "src/system/useResourceList/useBankTransactionGridProps.tsx";
import {
  DEVELOPER_COLUMNS,
  urlStateValidationSchema,
} from "src/system/useResourceList/useResourceList.constants.ts";
import { prepareSortModel } from "src/system/useResourceList/utils/prepareSortModel.ts";
import { defaultListSort } from "src/system/constants.ts";

export type UseResourceListProps<
  Row extends GridValidRowModel = GridValidRowModel,
> = {
  resource: string;
  filter?: GridFilterModel;
  include?: string[];
  disableStatePersistenceToUrl?: boolean;
  expandRowOnRowClick?: boolean;
  withoutDescendants?: boolean;
  queryOptions?: Omit<
    UseQueryOptions<
      ValidGetListResponse<Row>,
      any,
      ValidGetListResponse<Row>,
      any
    >,
    "queryFn" | "queryKey"
  >;
  saveToDatabase?: boolean;

  disableAutomaticDataLoading?: boolean;
} & Partial<DataGridProps<Row>>;

export const useResourceList = <
  Row extends GridValidRowModel = GridValidRowModel,
>(
  props: UseResourceListProps<Row>,
) => {
  const {
    disableAutomaticDataLoading = false,
    disableStatePersistenceToUrl = false,
    loading: loadingProp,
    withoutDescendants = false,
    expandRowOnRowClick = false,
    resource,
    include: includeProp,
    filter: staticFilter,
    saveToDatabase = true,
    onToolbarRefresh: onToolbarRefreshProp,
    apiRef: apiRefProp,
    queryOptions,
    onRowClick,
    ...restProps
  } = props;
  const { router } = useDataRouterContext();
  const pendingUrlState = useRef<UrlState>();

  const { data: user } = useUser();
  const snackbar = useSnackbar();
  const { data: globalSettings } = useGlobalSettings();

  const [searchParams, setSearchParams] = useSearchParams();

  const value = searchParams.get("option") ?? "default";
  const handleSelect = (value: string) => {
    if (value === "default") {
      searchParams.delete("option");
      setSearchParams(searchParams);
    } else {
      searchParams.set("option", value);
      setSearchParams(searchParams);
    }
  };
  const option = searchParams.get("option") ?? "default";

  const { t } = useErpTranslation({
    resource,
    type: "list",
  });

  const clientConfig = useClientConfig();
  const { data: listConfig, refetch: refetchListConfig } = useSuspenseQuery({
    ...resourceListConfigQuery({
      resource,
      clientConfig,
    }),
  });

  const {
    getColumns,
    mapResourceToRow,
    toolbarStartAdditionalElements: toolbarStartAdditionalElementsProp = [],
    toolbarEndAdditionalElements: toolbarEndAdditionalElementsProp = [],
    includes: includesFromConfig,
  } = listConfig;

  const { refetch: refetchTranslations } = useSuspenseQuery(
    listTranslationsQuery({
      resource,
    }),
  );

  const contentLocale = useContentLocale()!;

  const localApiRef = useGridApiRef();
  const apiRef = apiRefProp || localApiRef;

  const storageKeys = useResourceStorageKeys({
    resource,
  });

  const {
    setValue: setUserDataExpandedRowsState,
    clear: clearExpandedRowsState,
    getValue: getUserExpandedRowsState,
  } = useUserDataState<DataGridExpandedRowsPersistedState>({
    user: user!,
    validationSchema: DataGridExpandedRowsSchema,
    key: storageKeys.listExpandedRows,
    disableSavingToDatabase: true,
  });

  const { value: userDataState, setValue: setUserDataState } =
    useUserDataState<PersistedGridState>({
      user: user!,
      key: storageKeys.listState,
      validationSchema: DataGridPersistedStateSchema,
      disableSavingToDatabase: !saveToDatabase,
    });

  const initialState = hideIdColumnInInitialState(userDataState);
  const defaultSort = defaultListSort[resource] || {};

  const defaultSortModel: GridSortModel = [];
  const defaultPaginationModel = {
    page: 0,
    pageSize: 0,
  };
  const defaultFilterModel = {
    items: [],
  };
  Object.entries(defaultSort).forEach(([field, dir]) => {
    if (dir) {
      defaultSortModel.push({
        field: field,
        sort: dir,
      });
    }
  });

  const [gridUrlState, setGridUrlState] = useUrlState<UrlState>(
    router,
    {
      [ResourceListSearchParams.FilterModel]: {},
      [ResourceListSearchParams.PageSize]: defaultPaginationModel.pageSize,
      [ResourceListSearchParams.Page]: defaultPaginationModel.page,
      [ResourceListSearchParams.SortModel]:
        convertGridSortModelToUrlSortModel(defaultSortModel),
    },
    {
      disablePersistence: disableStatePersistenceToUrl,
      validationSchema: urlStateValidationSchema,
    },
  );

  const {
    filterModel: appliedFilterModel,
    paginationModel,
    sortModel,
    notAppliedFilterModel,
  } = convertUrlStateToGridState(gridUrlState);

  const setFilterModel = (model: GridFilterModel) => {
    const modelWithoutIds = filterModelWithoutIds(model);

    if (deepEq(notAppliedFilterModel, model)) {
      return;
    }

    const nextUrlState = {
      [ResourceListSearchParams.NotAppliedFilterModel]:
        convertGridFilterModelToUrlFilterModel(modelWithoutIds),
    };
    pendingUrlState.current = {
      ...pendingUrlState.current,
      ...nextUrlState,
    };

    setGridUrlState({
      ...pendingUrlState.current,
    });
    setTimeout(() => {
      pendingUrlState.current = {};
    });
  };

  const setSortModel = (model: GridSortModel) => {
    clearExpandedRowsState();

    let finalModel = model;

    const saveDefaultSortModel = [];
    defaultSortModel.forEach((item) => {
      const column = columns.find((column) => column.field === item.field);
      if (!column) {
        logger.warn(
          `There is no column for [${item.field} ${item.sort}] sort]`,
        );
      } else {
        saveDefaultSortModel.push(item);
      }
    });

    if (saveDefaultSortModel.length !== 0 && model.length === 0) {
      finalModel = [
        {
          ...sortModel[0],
          sort: sortModel[0].sort === "asc" ? "desc" : "asc",
        },
      ];
    }

    setGridUrlState(
      {
        [ResourceListSearchParams.SortModel]:
          convertGridSortModelToUrlSortModel(finalModel),
      },
      {
        onFinish: () => {
          refetchData();
        },
      },
    );
  };

  const setAppliedFilterModel = (appliedFilterModel: GridFilterModel) => {
    clearExpandedRowsState();

    const nextUrlState = {
      [ResourceListSearchParams.Page]: 0,
      [ResourceListSearchParams.FilterModel]:
        convertGridFilterModelToUrlFilterModel(appliedFilterModel),
      [ResourceListSearchParams.FilterModelLogicOperator]:
        appliedFilterModel.logicOperator,
    };
    pendingUrlState.current = {
      ...pendingUrlState.current,
      ...nextUrlState,
    };
    refetchData();
    setGridUrlState({
      ...pendingUrlState.current,
    });
    setTimeout(() => {
      pendingUrlState.current = {};
    });
  };

  const setPaginationModel = (
    model: GridPaginationModel,
    details: GridCallbackDetails,
  ) => {
    if (details.reason !== "autoPageSize") {
      clearExpandedRowsState();
    }

    const nextPage = Math.max(0, model.page);
    const nextPageSize = Math.max(model.pageSize, 0);
    const prevPage = gridUrlState[ResourceListSearchParams.Page];
    const prevPageSize = gridUrlState[ResourceListSearchParams.PageSize];

    if (nextPage === prevPage && prevPageSize === nextPageSize) {
      return;
    }
    setGridUrlState(
      {
        [ResourceListSearchParams.Page]: Math.max(0, model.page),
        [ResourceListSearchParams.PageSize]: Math.max(model.pageSize, 0),
      },
      {
        onFinish: () => {
          refetchData();
        },
      },
    );
  };

  const dataLoadingEnabledByForce =
    gridUrlState[ResourceListSearchParams.DataLoadingEnabledByForce];

  const dataLoadingEnabled =
    !!paginationModel.pageSize &&
    (dataLoadingEnabledByForce || !disableAutomaticDataLoading);

  const combinedInclude = unique([
    ...(includeProp || []),
    ...(includesFromConfig || []),
  ]);

  const queryResult = useList<Row>({
    resource: props.resource,
    locale: contentLocale,
    invalidatePreviousQuery: true,
    include: combinedInclude,
    withAggregation: true,

    filter: prepareFilterModel(
      {
        ...appliedFilterModel,
        items: [...appliedFilterModel.items, ...(staticFilter?.items || [])],
      },
      getColumns({
        t,
        data: undefined,
        option,
        clientConfig,
        globalSettings,
      }),
    ),
    sort: prepareSortModel(
      sortModel,
      getColumns({
        t,
        data: undefined,
        option,
        clientConfig,
        globalSettings,
      }),
    ),
    option,
    page: {
      number: paginationModel.page + 1,
      size: paginationModel.pageSize,
    },
    queryOptions: {
      structuralSharing: false,
      enabled: dataLoadingEnabled,
      staleTime: Infinity, // set to 'Infinity' to prevent query from automatically refetching on mount
      gcTime: Infinity,
      refetchOnMount: true,
      placeholderData: keepPreviousData,
      ...queryOptions,
    },
  });

  const {
    data: listData,
    isFetching,
    refetch: refetchData,
    error: listError,
  } = queryResult;

  const columns = useMemo(() => {
    const cols = getColumns({
      t: t,
      data: listData,
      globalSettings: globalSettings,
      option,
      clientConfig,
      staticFilter,
    }).map(({ headerName, field, ...restColumn }) => ({
      headerName: headerName ?? t(`table.columns.${field}`),
      field,
      ...restColumn,
    }));

    return cols.filter((column) => !DEVELOPER_COLUMNS.includes(column.field));
  }, [getColumns, listData, t, option, globalSettings, clientConfig]);

  const handleRecoverFromError = () => {
    apiRef.current.setSortModel(defaultSortModel);
    apiRef.current.setPaginationModel(defaultPaginationModel);
    apiRef.current.setFilterModel(defaultFilterModel);
    setAppliedFilterModel(defaultFilterModel);

    refetchData();
  };

  const { rows, error: mapRowsError } = createMapRowsResult({
    mapResourceToRow:
      getDataGridCustomMapper({
        resource,
      }) || mapResourceToRow,
    data: listData,
    option,
  });

  const [showedErrors] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (mapRowsError && mapRowsError instanceof Error) {
      if (showedErrors.has(mapRowsError.message)) {
        return;
      }
      snackbar.error({
        flash: false,
        message: "Failed mapping list data: " + mapRowsError.message,
      });
      showedErrors.add(mapRowsError.message);
      logger.error(mapRowsError);
    }
  }, [mapRowsError, snackbar, showedErrors]);

  const hideToolbarPagination = !dataLoadingEnabled;

  const loading = isFetching || loadingProp;

  const handleToolbarRefresh = () => {
    clearExpandedRowsState();
    refetchData();
    const mergedModel = mergeFilterModels(
      notAppliedFilterModel,
      appliedFilterModel,
    );
    setAppliedFilterModel(mergedModel);
    setFilterModel(defaultFilterModel);
    setSortModel(defaultSortModel);

    onToolbarRefreshProp?.();
    if (import.meta.env.DEV) {
      refetchListConfig();
      refetchTranslations();
    }
  };

  const enableDataLoading = () => {
    setGridUrlState({
      [ResourceListSearchParams.DataLoadingEnabledByForce]: true,
    });
  };

  const isGroupExpandedByDefaultInternal = useCallback(
    (node: GridGroupNode) => {
      const stateFromStorage = getUserExpandedRowsState() || [];

      return stateFromStorage.includes(node.id);
    },
    [getUserExpandedRowsState],
  );

  const handleStateChange: DataGridPremiumProps<Row>["onStateChange"] = (
    state,
  ) => {
    const orderedFields = unique([
      // make sure grouping column always at the beginning
      // fixes https://link340.lcube-server.de/exonn-erp2/erp2-frontend/-/issues/88
      ...(props.treeData ? [GRID_TREE_DATA_GROUPING_FIELD] : []),
      ...(state?.columns?.orderedFields || []),
    ]);

    const finalState = {
      ...state,
      columns: {
        ...state.columns,
        orderedFields,
      },
    };
    setUserDataState(mapGridStateToPersistedState(finalState));
  };

  useEffect(() => {
    const onRowExpansionChange: GridEventListener<"rowExpansionChange"> = (
      params,
    ) => {
      const { childrenExpanded, id } = params;
      const prevExpanded = getUserExpandedRowsState() || [];

      const nextExpanded = childrenExpanded
        ? [...prevExpanded, id]
        : removeItem(prevExpanded, id);

      setUserDataExpandedRowsState(nextExpanded);
    };
    return apiRef.current?.subscribeEvent?.(
      "rowExpansionChange",
      onRowExpansionChange,
    );
  }, [apiRef, getUserExpandedRowsState, setUserDataExpandedRowsState]);

  const rowCount = listData ? listData.meta.total : 0;

  const productGridProps = useProductGridProps({
    resource,
    locale: contentLocale,
    withoutDescendants,
    apiRef,
    expandRowOnRowClick,
    onRowClick,
    appliedFilterModel: appliedFilterModel,
    includes: combinedInclude,
  });

  const bankTransactionGridProps = useBankTransactionGridProps({
    resource,
  });

  const b2bGateOrderGridProps = useB2bGateOrderGridProps({
    resource,
    locale: contentLocale,
    apiRef,
    expandRowOnRowClick,
    onRowClick,
    appliedFilterModel: appliedFilterModel,
    includes: combinedInclude,
  });

  const toolbarStartAdditionalElements = [
    ...(resource === ResourceEnum.Stock
      ? [
          <SelectV2
            isButton={true}
            value={value}
            options={[
              {
                value: "default",
                label: t("options.view1"),
              },
              {
                value: "warehouse-place",
                label: t("options.view2"),
              },
              {
                value: "warehouse",
                label: t("options.view3"),
              },
              {
                value: "owner",
                label: t("options.view4"),
              },
            ]}
            onChange={handleSelect}
            startAdornment={<i className={"icon-15-data_rprd"} />}
            selectContainerStyle={{ width: "200px" }}
          />,
        ]
      : []),
    ...toolbarStartAdditionalElementsProp,
  ];

  const toolbarEndAdditionalElements =
    typeof toolbarEndAdditionalElementsProp === "function"
      ? toolbarEndAdditionalElementsProp(queryResult.data)
      : toolbarEndAdditionalElementsProp;

  const dataGridProps: DataGridProps<Row> = {
    rowCount,
    sortingMode: "server",
    paginationMode: "server",
    filterMode: "server",
    rows: listError ? [] : rows,
    onRowClick,
    appliedFilterModel: appliedFilterModel,
    onAppliedFilterModelChange: setAppliedFilterModel,
    disableVirtualization: true,
    columns,
    loading,
    initialState,
    showNoInitialDataLoadedOverlay: disableAutomaticDataLoading,
    onLoadInitialData: enableDataLoading,
    onToolbarRefresh: handleToolbarRefresh,
    onStateChange: handleStateChange,
    toolbarStartAdditionalElements,
    toolbarEndAdditionalElements,
    isGroupExpandedByDefault: isGroupExpandedByDefaultInternal,
    filterModel: notAppliedFilterModel,
    sortModel: sortModel,
    noRowsOverlayTitle: t(
      `noRows`,
      t("noRows", {
        ns: TranslationNamespaceEnum.DataGridComponent,
      }),
      {
        ns: getResourceI18nNamespace(resource),
      },
    ),
    disableMultipleRowSelection: true,
    paginationModel: paginationModel,
    onPaginationModelChange: setPaginationModel,
    onSortModelChange: setSortModel,
    onFilterModelChange: setFilterModel,
    onRecoverFromError: handleRecoverFromError,
    error: listError,
    hideToolbarPagination,
    createToolbarNewRecordText: t(`new`, {
      ns: getResourceI18nNamespace(resource),
    }),

    hideToolbarCsvButton: true,

    apiRef,
    autoPageSize: true,
    ...productGridProps,
    ...b2bGateOrderGridProps,
    ...bankTransactionGridProps,
    ...restProps,
  };

  return {
    dataGridProps,
    queryResult,
    key: option,
  };
};
